import { PREVIEW_OFFERS_REQUEST_KEY } from './constants';
import { addPreviewOffers, failurePreviewOffers, requestPreviewOffers, successPreviewOffers } from './previewOffers';
import {
  selectPinOffersInListing,
  selectPreviewOfferIdsToLoad,
  selectPreviewRealtyIds,
} from '../../selectors/previewOffers';
import { IApplicationState, TThunkAction } from '../../types/redux';
import { isRequestCancelled } from '../../utils/isRequestCancelled';
import { getJsonQueryWithoutRequestOnlyTerms } from '../../utils/jsonQuery/getJsonQueryWithoutRequestOnlyTerms';
import { loadListingOffersByIds } from '../listingOffers/loadListingOffersByIds';

/** Пока шёл запрос, пользователь мог выбрать другой пин: ответ нужен, только если офферы всё ещё в выбранном пине. */
function isStillSelected(state: IApplicationState, realtyIds: number[]): boolean {
  const selectedIds = selectPreviewRealtyIds(state);

  return realtyIds.every(realtyId => selectedIds.includes(realtyId));
}

/**
 * Собирает объявления пина в хранилище превью: что уже загрузил листинг - копирует,
 * недостающее догружает одним запросом. Почему копия, а не listingOffers - см. IPreviewOffersState.
 */
export const loadPreviewOffers =
  (realtyIds: number[]): TThunkAction<Promise<void>> =>
  async (dispatch, getState, context) => {
    // Оба списка считаем из одного стейта, поэтому порядок диспатчей ниже на них не влияет.
    const state = getState();
    const pinOffersInListing = selectPinOffersInListing(state, realtyIds);
    const idsToLoad = selectPreviewOfferIdsToLoad(state, realtyIds);

    if (pinOffersInListing.length > 0) {
      dispatch(addPreviewOffers(pinOffersInListing));
    }

    if (idsToLoad.length === 0) {
      return;
    }

    dispatch(requestPreviewOffers());
    // Новый запрос перебивает предыдущий: поздний ответ прошлого пина не нужен ни в стейте, ни в логе.
    const signal = context.cancellationTokenManager?.getSignalAndCancelPrevious(PREVIEW_OFFERS_REQUEST_KEY);

    try {
      const { offers } = await dispatch(
        loadListingOffersByIds({
          baseJsonQuery: getJsonQueryWithoutRequestOnlyTerms(state.filters.jsonQuery),
          offerIds: idsToLoad,
          queryString: state.queryString,
          signal,
        }),
      );

      if (isStillSelected(getState(), idsToLoad)) {
        dispatch(successPreviewOffers(offers));
      }
    } catch (error) {
      // Отменённый запрос - не ошибка: его сменил новый или превью закрыли.
      if (isRequestCancelled(error)) {
        return;
      }

      // Ошибку уже залогировал loadListingOffersByIds.
      if (isStillSelected(getState(), idsToLoad)) {
        dispatch(failurePreviewOffers());
      }
    }
  };
